/**
 * The layer stack, which is browser history (D02).
 *
 * - Open is a PUSH (`useOpenLayer`), and so is switching the base screen.
 * - Close is `history.back()` (`useCloseLayer`), so Back, Escape and a close
 *   button all pop the same entry and history never gains a duplicate.
 * - When nothing of this app sits behind the current entry (a deep link
 *   opened cold, a push-notification tap), close falls back to a REPLACE with
 *   the layer's params removed. `history.back()` there would leave the app.
 * - `card` and the picker's `muscle` are written with REPLACE
 *   (`useReplaceParam`), a one-shot param is cleared with REPLACE once acted
 *   on (`useConsumeOneShot`), and switching one layer for another in place is
 *   a REPLACE as well (`useReplaceLayer`). Those are the only replaces besides
 *   the cold close.
 *
 * "Is there an in-app entry behind" is tracked here rather than read from
 * `useCanGoBack` (experimental): every open writes `mpgDepth` = previous + 1
 * into the history entry's state, and every replace carries the state over.
 * `mpgOrder` records which of sheet and overlay was opened last, because the
 * coach and a sheet may coexist (D04) and Back must close the top one.
 * `mpgOpenedAt` records the depth at which each slot's layer was PUSHED: a
 * sheet pushed over another sheet is a new layer on top of it, which the
 * shell keeps mounted underneath (`D225`), while a push that leaves the
 * sheet as it was — the trainer opened over it — is the same layer.
 */
import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { useLayerPresence } from "../ui/overlay/presence";
import type { Overlay, Search } from "./search";

export type LayerKind = "sheet" | "overlay" | "screen";
type StackKind = Exclude<LayerKind, "screen">;

declare module "@tanstack/react-router" {
  interface HistoryState {
    /** How many entries this app pushed before this one (0 = cold entry). */
    mpgDepth?: number;
    /** The depth at which each slot's current layer was pushed (`D225`). */
    mpgOpenedAt?: OpenedAt;
    /** Opening order of the sheet and overlay layers, oldest first. */
    mpgOrder?: StackKind[];
    /** Steps taken inside the top sheet (`useStepLayer`, `D231`). */
    mpgSteps?: number;
  }
}

/** Per slot, the `mpgDepth` of the entry that pushed its current layer. */
export type OpenedAt = Partial<Record<StackKind, number>>;

/** The history-state fields this module reads. */
export interface LayerState {
  mpgDepth?: number;
  mpgOpenedAt?: OpenedAt;
  mpgOrder?: readonly StackKind[];
  /**
   * Steps taken inside the top sheet since it opened ({@link useStepLayer}),
   * per entry: what closing it goes back past. Absent is none, which is
   * every layer but the exercise sheet browsing its neighbours.
   */
  mpgSteps?: number;
}

/**
 * The overlay actually shown: `diagnostics` exists only in DEV builds, so in
 * production it is as if no overlay were set.
 */
export function overlayOf(
  search: Pick<Search, "overlay">,
  dev: boolean = import.meta.env.DEV
): Overlay | undefined {
  return search.overlay === "diagnostics" && !dev ? undefined : search.overlay;
}

/**
 * Which layer is on top. Sheet over overlay over screen — with one exception:
 * the coach overlay and a sheet coexist (side by side on desktop, D04), and
 * then whichever was opened LAST is on top, read from `order`. Without an
 * order (a cold deep link carrying both) the sheet wins.
 */
export function topLayer(
  search: Pick<Search, "overlay" | "sheet">,
  order: readonly StackKind[] = [],
  dev: boolean = import.meta.env.DEV
): LayerKind {
  const overlay = overlayOf(search, dev);
  const hasSheet = search.sheet !== undefined;
  if (hasSheet && overlay === "coach") {
    // `mpgOrder` holds only "sheet" and "overlay", oldest first.
    const last = order.at(-1);
    return last ?? "sheet";
  }
  if (hasSheet) {
    return "sheet";
  }
  if (overlay !== undefined) {
    return "overlay";
  }
  return "screen";
}

/** The params that belong to a layer, cleared when it closes. */
const LAYER_PARAMS = {
  overlay: [
    "overlay",
    "ask",
    "send",
    "about",
    "conversation",
    "subject",
    "q",
    "section",
    "edit",
    "card",
    "period",
    "assessment",
  ],
  sheet: [
    "sheet",
    "ex",
    "tab",
    "prev",
    "door",
    "session",
    "set",
    "protocol",
    "exclude",
    "item",
    "muscle",
    "pick",
    "never",
    "draft",
    "span",
    "at",
    "group",
    "student",
    "topic",
    "club",
  ],
} as const satisfies Record<StackKind, readonly (keyof Search)[]>;

/**
 * The cold-close fallback for a layer: the same search minus that layer's
 * params. Closing the screen layer goes to Today.
 */
export function closeFallbackFor(kind: LayerKind): Partial<Search> {
  if (kind === "screen") {
    return { screen: undefined };
  }
  const fallback: Partial<Search> = {};
  for (const key of LAYER_PARAMS[kind]) {
    fallback[key] = undefined;
  }
  return fallback;
}

/** The next `mpgOrder` after opening `params` over `order`. */
export function nextOrder(
  order: readonly StackKind[],
  params: Partial<Search>
): StackKind[] {
  let next = [...order];
  for (const kind of ["overlay", "sheet"] as const) {
    if (!(kind in params)) {
      continue;
    }
    next = next.filter((k) => k !== kind);
    if (params[kind] !== undefined) {
      next.push(kind);
    }
  }
  return next;
}

/**
 * The next `mpgOpenedAt` after pushing `params` at `depth`: a slot `params`
 * names is opened there, a slot it clears is forgotten, and the rest carry
 * over — so the coach pushed over the builder leaves the builder the layer
 * it was.
 */
export function nextOpenedAt(
  openedAt: OpenedAt,
  params: Partial<Search>,
  depth: number
): OpenedAt {
  const next: OpenedAt = { ...openedAt };
  for (const kind of ["overlay", "sheet"] as const) {
    if (!(kind in params)) {
      continue;
    }
    if (params[kind] === undefined) {
      delete next[kind];
    } else {
      next[kind] = depth;
    }
  }
  return next;
}

/** The history state of a PUSH of `params` over an entry holding `prev`. */
export function pushedState<T extends LayerState>(
  prev: T,
  params: Partial<Search>
): T & { mpgDepth: number; mpgOpenedAt: OpenedAt; mpgOrder: StackKind[] } {
  const depth = (prev.mpgDepth ?? 0) + 1;
  // A new sheet or overlay over a stepped sheet starts with no steps of its
  // own; Back to the sheet restores that entry's count with its state.
  const { mpgSteps: _steps, ...kept } = prev;
  return {
    ...(kept as T),
    mpgDepth: depth,
    mpgOpenedAt: nextOpenedAt(prev.mpgOpenedAt ?? {}, params, depth),
    mpgOrder: nextOrder(prev.mpgOrder ?? [], params),
  };
}

/** Whether an entry this app pushed sits behind the current one. */
export function hasInAppHistory(state: LayerState | undefined): boolean {
  return (state?.mpgDepth ?? 0) > 0;
}

/** Drops keys whose value is `undefined`, so the URL does not carry them. */
export function mergeSearch(prev: Search, params: Partial<Search>): Search {
  const next: Record<string, unknown> = { ...prev, ...params };
  for (const key of Object.keys(next)) {
    if (next[key] === undefined) {
      delete next[key];
    }
  }
  return next as Search;
}

/** Opens a layer, or switches the screen: always a PUSH. */
export function useOpenLayer(): (params: Partial<Search>) => void {
  const navigate = useNavigate();
  return useCallback(
    (params: Partial<Search>) => {
      navigate({
        to: "/",
        search: (prev: Search) => mergeSearch(prev, params),
        state: (prev) => pushedState(prev, params),
      });
    },
    [navigate]
  );
}

/** A REPLACE that keeps the entry's layer state. Internal to the helpers. */
function useReplace(): (
  params: Partial<Search>,
  order?: (prev: readonly StackKind[]) => StackKind[]
) => void {
  const navigate = useNavigate();
  return useCallback(
    (params, order) => {
      navigate({
        to: "/",
        replace: true,
        search: (prev: Search) => mergeSearch(prev, params),
        state: (prev) =>
          order ? { ...prev, mpgOrder: order(prev.mpgOrder ?? []) } : prev,
      });
    },
    [navigate]
  );
}

/**
 * A STEP inside the top layer (`D231`): a PUSH that changes what the layer
 * shows — another exercise in the same exercise sheet — without opening a
 * new layer. It names neither `sheet` nor `overlay`, so the layer keeps the
 * depth it was opened at (`mpgOpenedAt`) and stays the same instance: Back
 * walks the steps one by one, and closing the layer leaves them all at once
 * ({@link useCloseLayer}). How native sheets navigate inside themselves.
 */
export function useStepLayer(): (
  params: Omit<Partial<Search>, "overlay" | "screen" | "sheet">
) => void {
  const navigate = useNavigate();
  return useCallback(
    (params) => {
      navigate({
        to: "/",
        search: (prev: Search) => mergeSearch(prev, params),
        state: (prev) => ({
          ...pushedState(prev, params),
          mpgSteps: (prev.mpgSteps ?? 0) + 1,
        }),
      });
    },
    [navigate]
  );
}

/**
 * How many entries closing the top SHEET goes back: one, plus every step
 * taken inside it ({@link useStepLayer}) — so the X and the swipe leave the
 * sheet at once however far the person browsed. Only steps count: a page
 * pushed inside the profile overlay is its own layer of history and closes
 * one at a time, as it always has. Anything else goes back one.
 */
export function closeDistance(
  state: LayerState | undefined,
  fallback: Partial<Search>
): number {
  const steps = "sheet" in fallback ? (state?.mpgSteps ?? 0) : 0;
  return 1 + Math.min(steps, Math.max(0, (state?.mpgDepth ?? 1) - 1));
}

/**
 * Closes the top layer: back to the entry it was opened over when this app
 * pushed it — past every step taken inside it ({@link closeDistance}) —
 * otherwise a REPLACE applying `fallback` (usually `closeFallbackFor(kind)`).
 *
 * Called from a layer that is not the top one — covered by a sheet pushed
 * over it, or already leaving — it does nothing (`D225`). Such a layer's
 * Base UI root still hears Escape and an outside press, and a second
 * `history.back()` for the same press would close the layer underneath too.
 */
export function useCloseLayer(): (fallback: Partial<Search>) => void {
  const router = useRouter();
  const replace = useReplace();
  const notTop = useLayerPresence().frozen !== undefined;
  return useCallback(
    (fallback: Partial<Search>) => {
      if (notTop) {
        return;
      }
      const { state } = router.state.location;
      if (hasInAppHistory(state)) {
        router.history.go(-closeDistance(state, fallback));
        return;
      }
      replace(fallback, (order) => nextOrder(order, fallback));
    },
    [router, replace, notTop]
  );
}

/**
 * Leaves a PUSHED screen — the workout — the way Back would (`D193`): back
 * to the entry this app pushed it over, else Today with REPLACE. Minimising
 * the workout, cancelling the readiness check and leaving the after-session
 * view all used to PUSH Today, so each one stacked a new entry, Back from
 * Today returned into the workout, and a direction-aware transition would
 * have played them as "forward". The one-shots riding on the workout's entry
 * go with it.
 */
export function useLeaveScreen(): () => void {
  const closeLayer = useCloseLayer();
  return useCallback(
    () =>
      closeLayer({
        ...closeFallbackFor("screen"),
        day: undefined,
        start: undefined,
      }),
    [closeLayer]
  );
}

/**
 * Switches one layer for another IN PLACE: a REPLACE that merges `params` and
 * moves whichever of sheet and overlay it writes to the end of `mpgOrder`.
 *
 * `mpgDepth` is carried over untouched, because the entry behind this one is
 * unchanged — so the layer that lands closes to wherever the layer it replaced
 * would have closed to. That is the whole point: a confirm opened from a sheet
 * with `useOpenLayer` would close BACK ONTO that sheet, which is how ending a
 * workout from the exercise list left the list open over the after-view
 * (`features/workout/screens/exercise-list.tsx`).
 */
export function useReplaceLayer(): (params: Partial<Search>) => void {
  const replace = useReplace();
  return useCallback(
    (params: Partial<Search>) =>
      replace(params, (order) => nextOrder(order, params)),
    [replace]
  );
}

/**
 * Writes `card`, `muscle` or `tab` with REPLACE: scrolling, changing the
 * picker's muscle, or the exercise sheet's tab (`D199`), is not history —
 * nor is the report's span or a step to its neighbour (`D252`).
 */
export function useReplaceParam(): (
  params: Partial<Pick<Search, "at" | "card" | "muscle" | "span" | "tab">>
) => void {
  const replace = useReplace();
  return useCallback((params) => replace(params), [replace]);
}

/**
 * `day` rides beside `start` on the base screen, so like `start` it is not a
 * sheet param: a sheet closed over the readiness check must not drop the day
 * and leave `start` to begin today's workout instead.
 */
export type OneShot =
  | "start"
  | "day"
  | "ask"
  | "send"
  | "about"
  | "q"
  | "protocol"
  | "exclude";

/** The REPLACE that clears one one-shot param, and nothing else. */
export function consumedOneShot(name: OneShot): Partial<Search> {
  return { [name]: undefined };
}

/**
 * Reads a one-shot param. Call `consume()` once the caller has acted on it;
 * it clears the param with REPLACE, so Back does not replay it.
 */
export function useConsumeOneShot<K extends OneShot>(
  name: K
): [value: Search[K], consume: () => void] {
  const value = useAppSearch()[name];
  // Leaving (or covered, `D225`), not "not present": a layer is also not
  // present for the one commit in which it ARRIVES (`app/shell.tsx`), and a
  // consume that changed identity across that commit would re-run every
  // effect keyed on it — the draft review's generation among them (`T-145`).
  const leaving = useLayerPresence().frozen !== undefined;
  const replace = useReplace();
  const consume = useCallback(() => {
    // A leaving layer reads its frozen search (below): the param it would
    // clear is already gone from the URL, and a REPLACE now would be a
    // navigation nobody asked for.
    if (!leaving) {
      replace(consumedOneShot(name));
    }
  }, [replace, name, leaving]);
  return [value, consume];
}

/**
 * The current search, typed. Features read the URL only through this.
 *
 * Inside a layer that is playing its exit, it is the search that layer saw
 * LAST while it was open (`ui/overlay/presence.tsx`, `D193`): the live URL
 * no longer names it — `ex` or `session` already gone — and reading that
 * would draw "not found" on the layer as it slides away. The same holds for
 * a layer covered by a sheet pushed over it (`D225`): the URL is the top
 * layer's, and the catalogue under an exercise would read the exercise's
 * cleared pick context as plain browsing and reset its list.
 */
export function useAppSearch(): Search {
  const live = useSearch({ from: "/" });
  const { frozen } = useLayerPresence();
  return frozen === undefined ? live : (frozen as Search);
}
